import { PDFDocument, PDFFont, rgb } from 'pdf-lib';
import * as XLSX from 'xlsx';
import { embedBodyFonts, embedStandardFallbackFonts } from './pdfFonts';
import type { PdfExportQuality } from './pdfExportQuality';
import { layoutForQuality } from './pdfExportQuality';
import type { PageDims } from './pdfPageDims';

export function listSpreadsheetSheetNames(buffer: ArrayBuffer): string[] {
  const wb = XLSX.read(buffer, { type: 'array' });
  return wb.SheetNames ?? [];
}

export interface SpreadsheetPdfOptions {
  quality: PdfExportQuality;
  pageDims: PageDims;
  sheetIndex: number;
  fitPageWidth: boolean;
  gridlines: boolean;
  repeatHeader: boolean;
}

interface Cursor {
  doc: PDFDocument;
  page: ReturnType<PDFDocument['addPage']>;
  y: number;
  fonts: Awaited<ReturnType<typeof embedBodyFonts>>;
  layout: ReturnType<typeof layoutForQuality>;
  dims: PageDims;
  mono: PDFFont;
}

function pageBottom(c: Cursor): number {
  return c.layout.margin + c.layout.footerReserved;
}

function freshPage(c: Cursor): void {
  c.page = c.doc.addPage([c.dims.widthPt, c.dims.heightPt]);
  c.y = c.dims.heightPt - c.layout.margin;
}

function ensureVertical(c: Cursor, need: number): void {
  if (c.y - need < pageBottom(c)) {
    freshPage(c);
  }
}

function drawFooter(page: ReturnType<PDFDocument['getPage']>, index: number, total: number, font: PDFFont, margin: number): void {
  const label = `Page ${index + 1} of ${total}`;
  const size = 8.5;
  const w = font.widthOfTextAtSize(label, size);
  const pw = page.getWidth();
  page.drawText(label, {
    x: pw - margin - w,
    y: margin * 0.55,
    size,
    font,
    color: rgb(0.55, 0.55, 0.58),
  });
}

function applyFooters(doc: PDFDocument, font: PDFFont, margin: number): void {
  doc.getPages().forEach((p, i, arr) => drawFooter(p, i, arr.length, font, margin));
}

function colWidths(charsPerCol: number[], totalAvail: number): number[] {
  const weights = charsPerCol.map((n) => Math.max(n, 4));
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => (w / sum) * totalAvail);
}

export async function convertSpreadsheetBufferToPdf(buffer: ArrayBuffer, opts: SpreadsheetPdfOptions): Promise<Uint8Array> {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
  const names = wb.SheetNames;
  if (!names.length) throw new Error('Workbook has no sheets');

  const idx = Math.min(Math.max(0, opts.sheetIndex), names.length - 1);
  const sheetName = names[idx]!;
  const sheet = wb.Sheets[sheetName]!;

  const rowsRaw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }) as unknown[][];
  const table = rowsRaw.map((row) =>
    row.map((cell) => {
      if (cell === null || cell === undefined) return '';
      return String(cell);
    }),
  );

  const colCount = Math.max(1, ...table.map((r) => r.length));
  const normalized = table.map((r) => {
    const row = [...r];
    while (row.length < colCount) row.push('');
    return row;
  });

  const layout = layoutForQuality(opts.quality);
  const doc = await PDFDocument.create();
  let fonts;
  try {
    fonts = await embedBodyFonts(doc);
  } catch {
    fonts = await embedStandardFallbackFonts(doc);
  }
  const mono = fonts.mono;

  const dims = opts.pageDims;
  let fontSize = layout.metaSize;
  const padInner = opts.gridlines ? 5 : 4;

  const maxChars = (ci: number) =>
    normalized.reduce((m, row) => Math.max(m, row[ci]?.length ?? 0), 0);

  let widths: number[] = [];

  const innerAvail = () => Math.max(120, dims.widthPt - 2 * layout.margin - padInner * 2 * colCount);

  const recomputeLayout = (): number => {
    const chars = Array.from({ length: colCount }, (_, ci) => maxChars(ci));
    widths = colWidths(chars, innerAvail());
    let gridExtra = opts.gridlines ? (colCount + 1) * 0.35 : 0;
    let totalW = widths.reduce((a, b) => a + b, 0) + gridExtra;
    if (opts.fitPageWidth && totalW > dims.widthPt - 2 * layout.margin) {
      const scale = (dims.widthPt - 2 * layout.margin - 12) / totalW;
      fontSize *= Math.max(0.62, Math.min(1, scale));
    }
    const lh = fontSize * 1.35;
    gridExtra = opts.gridlines ? (colCount + 1) * 0.35 : 0;
    totalW = widths.reduce((a, b) => a + b, 0) + gridExtra;
    if (opts.fitPageWidth && totalW > dims.widthPt - 2 * layout.margin) {
      widths = widths.map((w) => (w * (dims.widthPt - 2 * layout.margin - gridExtra)) / (totalW - gridExtra));
    }
    return lh;
  };

  let rowHeight = recomputeLayout();

  const drawHeaderRow = (c: Cursor, header: string[]): void => {
    ensureVertical(c, rowHeight + 10);
    let x = c.layout.margin;
    const bottom = c.y - rowHeight;
    const top = c.y;
    header.forEach((cell, ci) => {
      const wCol = widths[ci] ?? 40;
      if (opts.gridlines) {
        c.page.drawRectangle({
          x,
          y: bottom,
          width: wCol,
          height: rowHeight,
          borderColor: rgb(0.82, 0.84, 0.88),
          borderWidth: 0.45,
          color: rgb(0.94, 0.96, 0.99),
        });
      }
      c.page.drawText(cell, {
        x: x + padInner,
        y: top - fontSize - 3,
        size: fontSize * 1.02,
        font: mono,
        color: rgb(0.08, 0.09, 0.12),
        maxWidth: wCol - padInner * 2,
        lineHeight: fontSize * 1.22,
      });
      x += wCol;
    });
    c.y = bottom - (opts.gridlines ? 3 : 8);
  };

  const drawBodyRow = (c: Cursor, row: string[]): void => {
    ensureVertical(c, rowHeight + 8);
    let x = c.layout.margin;
    const bottom = c.y - rowHeight;
    const top = c.y;
    row.forEach((cell, ci) => {
      const wCol = widths[ci] ?? 40;
      if (opts.gridlines) {
        c.page.drawRectangle({
          x,
          y: bottom,
          width: wCol,
          height: rowHeight,
          borderColor: rgb(0.9, 0.91, 0.93),
          borderWidth: 0.35,
          color: rgb(1, 1, 1),
        });
      }
      c.page.drawText(cell, {
        x: x + padInner,
        y: top - fontSize - 3,
        size: fontSize,
        font: mono,
        color: rgb(0.16, 0.17, 0.2),
        maxWidth: wCol - padInner * 2,
        lineHeight: fontSize * 1.22,
      });
      x += wCol;
    });
    c.y = bottom - (opts.gridlines ? 2 : 6);
  };

  const c: Cursor = {
    doc,
    page: doc.addPage([dims.widthPt, dims.heightPt]),
    y: dims.heightPt - layout.margin,
    fonts,
    layout,
    dims,
    mono,
  };

  ensureVertical(c, layout.sectionTitleSize + 10);
  c.page.drawText(`Sheet: ${sheetName}`, {
    x: c.layout.margin,
    y: c.y,
    size: layout.sectionTitleSize,
    font: fonts.bold,
    color: rgb(0.25, 0.27, 0.32),
  });
  c.y -= layout.sectionTitleSize + 10;

  rowHeight = recomputeLayout();

  const headerRow = normalized[0] ?? Array(colCount).fill('');
  const bodyRows = normalized.length > 1 ? normalized.slice(1) : [];

  drawHeaderRow(c, headerRow);

  bodyRows.forEach((row) => {
    if (opts.repeatHeader && c.y - rowHeight < pageBottom(c) + 48) {
      freshPage(c);
      rowHeight = recomputeLayout();
      drawHeaderRow(c, headerRow);
    }
    drawBodyRow(c, row);
  });

  if (!normalized.length) {
    ensureVertical(c, 40);
    c.page.drawText('(Empty sheet)', {
      x: c.layout.margin,
      y: c.y,
      size: layout.bodySize,
      font: fonts.regular,
      color: rgb(0.45, 0.46, 0.48),
    });
  }

  applyFooters(doc, fonts.regular, layout.margin);
  return doc.save();
}
